import { Checkbox } from '../../../components/ui/checkbox';
import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

const graphOptions = [
  { title: 'Perfect', color: 'var(--q3-green-normal)', checked: true },
  { title: 'Wasted', color: 'var(--q3-danger-normal)', checked: true },
  { title: 'Overtime', color: 'var(--q3-orange-normal)', checked: true },
  { title: 'Confused', color: 'var(--q3-neutral-light)', checked: true },
];

export default function SubjectAttemptAnalysisChart({ data, subjectMeta }) {
  const [chartData, setChartData] = useState(null);
  const [selectedGraphOptions, setSelectedGraphOptions] = useState(graphOptions);

  useEffect(() => {
    if (!data) return;

    const chartData = Object.entries(data)
      .filter(([key]) => key !== 'subjectMeta' && key !== 'overall')
      .map(([subject, stats]) => ({
        subject: subjectMeta[subject]?.title,
        Perfect: stats?.perfect || 0.15,
        Wasted: stats?.wasted || 0.15,
        Overtime: stats?.overtime || 0.15,
        Confused: stats?.confused || 0.15,
      }));

    setChartData(chartData);
  }, [data, subjectMeta]);

  const handleOnChange = (value, clickIndex) => {
    const newSet = selectedGraphOptions.map((option, index) => ({
      ...option,
      checked: index === clickIndex ? !option.checked : option.checked,
    }));
    setSelectedGraphOptions(newSet);
  };

  const CustomBarLabel = ({ x, y, width, value }) => {
    const actual = Math.round(value);
    const displayValue = value === 0.15 ? 0 : actual;
    if (!displayValue && value !== 0.15) return null;

    return (
      <g transform={`translate(${x + width / 2}, ${y - 8})`} textAnchor="middle">
        <text
          style={{
            fontWeight: 'bold',
            fontSize: '14px',
            fill: 'var(--q3-neutral-default)',
          }}
        >
          {displayValue}
        </text>
      </g>
    );
  };

  if (!chartData) return null;

  const visibleGraphOptions = selectedGraphOptions.filter((option) => option.checked);

  // Dynamically compute min width: subjects × bars/group × bar+gap width
  const minWidth = Math.max(chartData.length * visibleGraphOptions.length * 45, 400); // fallback to 400px

  return (
    <>
      <style jsx>{`
        .checkbox-custom[data-state='checked'] {
          background-color: var(--checkbox-bg) !important;
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:hover {
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:focus-visible {
          ring-color: var(--checkbox-border) !important;
        }
      `}</style>

      <div className="overflow-x-auto rounded-b-xl border-b border-[var(--q3-stroke-light)]">
        <div className="rounded-b-xl bg-[var(--q3-surface-default)] shadow-none border border-y-0 border-[var(--q3-stroke-light)]">
          <div className="p-4">
            <div className="flex justify-start gap-4 flex-wrap mb-4">
              {selectedGraphOptions.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center cursor-pointer gap-2 border-b-2"
                  style={{ borderBottomColor: item.checked ? item.color : 'transparent' }}
                >
                  <Checkbox
                    id={index}
                    checked={item.checked}
                    onCheckedChange={(value) => handleOnChange(value, index)}
                    className="checkbox-custom cursor-pointer rounded-[4px] border-[var(--q3-stroke-normal)]"
                    style={{
                      '--checkbox-bg': item.color,
                      '--checkbox-border': item.color,
                    }}
                    data-color={item.color}
                  />
                  <label htmlFor={index.toString()} className="text-[var(--q3-neutral-default)] cursor-pointer text-sm">
                    {item.title}
                  </label>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <div style={{ minWidth }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} barSize={25} barGap={5} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--q3-stroke-light)" />
                    <XAxis
                      dataKey="subject"
                      tick={{ fontSize: 14, fill: 'var(--q3-neutral-light)' }}
                      stroke="var(--q3-stroke-light)"
                      axisLine={{ stroke: 'var(--q3-stroke-light)' }}
                      interval={0}
                    />
                    <YAxis
                      domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
                      tickFormatter={(value) => `${Math.round(value)} Qs`}
                      allowDecimals={false}
                      tick={{ fontSize: 12, fill: 'var(--q3-neutral-default)' }}
                      width={50}
                      stroke="var(--q3-stroke-light)"
                      axisLine={{ stroke: 'var(--q3-stroke-light)' }}
                    />

                    {visibleGraphOptions.map((item) => (
                      <Bar
                        key={item.title}
                        dataKey={item.title}
                        fill={item.color}
                        label={<CustomBarLabel />}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
